import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { MEMBER_TABLE_VIEWS, DEFAULT_MEMBER_TABLE_VIEW } from '@constants';
import { REPORTS_TABLE_TEAMS_V2_PATH, HOME_PATH } from '@constants';
import { setTargetService } from 'actionCreators';
import {
  getTeamProfileUrl,
  getPendingEntitiesUrl,
  getNetworkLogUrl,
  getSandboxesPageUrl,
  getOverallIntegrationUrl,
  getSuperAdminPageUrl,
  getEntityErrorPageUrl,
  getPricingCalculatorUrl,
  standardizeUrl,
} from 'utils/url';
import { selectAuthUser } from 'selectors';
import * as Types from 'types';
import {
  getIntegrationHealthDeepLinkUrl,
  getIntegrationDetailsDeepLinkUrl,
  getReportTeamsPageDeepLinkUrl,
} from 'utils';
import { ValueOf } from 'type-fest';
import ApiClientManager from 'core/apiClient';
import qs from 'qs';
import { EntityErrorPageUrlQueryParams } from 'types';
import { fetchTempToken } from 'AuthModule/thunks';
import { useImpersonateToken } from 'AuthModule/hook/useImpersonateToken';
import useNavigationContext from './useNavigationContext';

/**
 * A hook that returns navigation actions.
 *
 * Navigation context (tenant/realmId) is automatically included from URL params
 * or global state when not explicitly provided. This ensures breadcrumbs and
 * API calls use the correct context.
 *
 * @example
 * ```tsx
 * const { navigateToTeamProfile, navigationContext } = useAppNavigation();
 *
 * // Auto-includes context from URL/global state:
 * navigateToTeamProfile({ teamId: 123 });
 *
 * // Or explicitly override:
 * navigateToTeamProfile({ teamId: 123, tenant: 'custom', realmId: 'prod' });
 * ```
 */
const useAppNavigation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectAuthUser);

  const { getImpersonateToken } = useImpersonateToken();

  // Get current navigation context from URL params or global state
  const navigationContext = useNavigationContext();

  const navigateToTeamProfile = ({
    teamId,
    memberTableView = DEFAULT_MEMBER_TABLE_VIEW,
    shouldOpenInAnotherTab = false,
    tenant,
    realmId,
    teamStatus,
  }: {
    teamId: number | string;
    shouldOpenInAnotherTab?: boolean;
    memberTableView?: ValueOf<typeof MEMBER_TABLE_VIEWS>;
    /** Optional tenant for the team - if not provided, uses current context */
    tenant?: string;
    /** Optional realmId for the team - if not provided, uses current context */
    realmId?: string;
    /** Optional team status to display on the team profile page */
    teamStatus?: { label: string; type: 'active' | 'deactivated' | 'scheduled' | 'deleted' };
  }) => {
    // Use provided values or fall back to navigation context
    const finalTenant = tenant ?? navigationContext.tenant;
    const finalRealmId = realmId ?? navigationContext.realmId;

    const url = getTeamProfileUrl({
      teamId: +teamId,
      memberTableView,
      tenant: finalTenant,
      realmId: finalRealmId,
    });
    if (shouldOpenInAnotherTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Pass team status via location state so it can be displayed on the profile page
      navigate(url, { state: teamStatus ? { teamStatus } : undefined });
    }
  };

  const navigateToIntegrationDetailsPage = useCallback(
    (
      teamId: number | string,
      targetService: string,
      targetServiceId: number,
      options?: { tenant?: string; realmId?: string },
    ) =>
      () => {
        // Use provided values or fall back to navigation context
        const finalOptions = {
          tenant: options?.tenant ?? navigationContext.tenant,
          realmId: options?.realmId ?? navigationContext.realmId,
        };
        navigate(
          getIntegrationDetailsDeepLinkUrl(teamId, targetService, targetServiceId, finalOptions),
        );
      },
    [navigate, navigationContext.tenant, navigationContext.realmId],
  );

  const navigateToGeneralIntegrationPage = useCallback(
    (targetService?: string, interval?: string) => {
      if (targetService && interval) {
        const queryparam = qs.stringify({ targetService, interval });
        navigate(getOverallIntegrationUrl(queryparam));
      } else {
        navigate(getOverallIntegrationUrl());
      }
    },
    [navigate],
  );

  const navigateToPendingEntitiesPage = useCallback(
    (
      teamId: number,
      targetService: string,
      targetServiceId: number,
      dataTypes: string[],
      mappingStatuses: string[],
    ) =>
      () => {
        dispatch(
          setTargetService({
            name: targetService,
            id: targetServiceId,
            pendingEntitiesDataTypes: dataTypes,
            pendingEntitiesMappingStatuses: mappingStatuses,
          }),
        );
        navigate(getPendingEntitiesUrl({ targetServiceId: targetServiceId, teamId }));
      },
    [dispatch, navigate],
  );

  const navigateToNetworkLog = useCallback(
    (mosaic_team_id: number, requestId: string | null, sessionId: string) => () => {
      window.open(getNetworkLogUrl({ mosaic_team_id, requestId, sessionId }), '_blank');
    },
    [],
  );

  const navigateToHome = useCallback(() => {
    navigate(HOME_PATH);
  }, [navigate]);

  const navigateToTeamsTable = (reportTableView?: string) => {
    navigate(getReportTeamsPageDeepLinkUrl({ reportTableView }));
  };

  const navigateToTeamsV2Table = () => {
    navigate(REPORTS_TABLE_TEAMS_V2_PATH);
  };

  const navigateToMosaicAppImpersonated = async ({
    accountId,
    teamId,
    urlDomain,
    tags,
    tenantName,
    realmId: overrideRealmId,
  }: {
    accountId: number;
    /** Team to verify the member's role on (GLX-1255 No Access block) */
    teamId: number;
    /** Optional override for where to impersonate (domain only) */
    urlDomain?: string;
    /** team tags, for user impersonate permission */
    tags: string[];
    /** Optional override headers/body for tenant + realm to impersonate into */
    tenantName?: string;
    realmId?: string;
  }) => {
    if (user) {
      try {
        const data = await getImpersonateToken({
          payload: {
            account_id: accountId,
            skip_onboarding: true,
            realm_id: overrideRealmId || ApiClientManager.getAuthDefaultRealmId(),
            super_admin_email: user.account.email,
            tags: tags || [],
            team_id: teamId,
          },
          config: {
            headers:
              tenantName || overrideRealmId
                ? ApiClientManager.buildUrlHeaders({
                    tenantName: tenantName,
                    realmId: overrideRealmId,
                  })
                : undefined,
          },
        });

        if (data && data.data.token) {
          dispatch(
            fetchTempToken({
              auth_token: data.data.token,
              tenantName,
              realmId: overrideRealmId || ApiClientManager.getAuthDefaultRealmId(),
              onSuccess: ({ response }) => {
                const realmId = overrideRealmId || ApiClientManager.getAuthDefaultRealmId();

                const queryParams = response.data
                  ? qs.stringify({
                      access_token: response.data.temp_token,
                      realm_id: realmId,
                    })
                  : qs.stringify({
                      impersonate: data.data.token,
                      realm_id: realmId,
                    });

                const urlDomainToUse = urlDomain
                  ? standardizeUrl({ url: urlDomain })
                  : ApiClientManager.getWebappUrlForTenant(tenantName);

                const redirectToMosaicUrl = `${urlDomainToUse}/?${queryParams}`;

                window.open(redirectToMosaicUrl);
              },
            }),
          );
        } else {
          throw new Error("Impersonate token response missing 'token'");
        }
      } catch (e: any) {
        console.error(e);
      }
    }
  };

  const navigateToIntegrationHealth = useCallback(
    ({
      targetServiceId,
      targetService,
      teamId,
    }: {
      targetServiceId: number | string;
      targetService: Types.TargetService;
      teamId: number;
    }) => {
      navigate(getIntegrationHealthDeepLinkUrl(teamId, targetService, targetServiceId));
    },
    [navigate],
  );

  const navigateToSandboxesTable = useCallback(() => {
    navigate(getSandboxesPageUrl());
  }, [navigate]);

  const navigateToSuperAdminTable = useCallback(() => {
    navigate(getSuperAdminPageUrl());
  }, [navigate]);

  const navigateToEntityErrorsTable = useCallback(
    ({
      openInNewTab,
      targetServiceId,
      entityId,
    }: {
      openInNewTab: boolean;
    } & EntityErrorPageUrlQueryParams) => {
      const url = getEntityErrorPageUrl({
        targetServiceId,
        entityId,
      });

      if (openInNewTab) {
        window.open(url, '_blank');
      } else {
        navigate(url);
      }
    },
    [navigate],
  );

  const navigateToPricingCalculator = useCallback(
    (params: Parameters<typeof getPricingCalculatorUrl>[0]) => {
      // Use provided values or fall back to navigation context
      const finalParams = {
        ...params,
        tenant: params.tenant ?? navigationContext.tenant,
        realmId: params.realmId ?? navigationContext.realmId,
      };
      navigate(getPricingCalculatorUrl(finalParams));
    },
    [navigate, navigationContext.tenant, navigationContext.realmId],
  );

  return {
    navigateToHome,
    navigateToTeamProfile,
    navigateToIntegrationDetailsPage,
    navigateToNetworkLog,
    navigateToPendingEntitiesPage,
    navigateToMosaicAppImpersonated,
    navigateToTeamsTable,
    navigateToIntegrationHealth,
    navigateToGeneralIntegrationPage,
    navigateToSandboxesTable,
    navigateToSuperAdminTable,
    navigateToEntityErrorsTable,
    navigateToPricingCalculator,
    navigateToTeamsV2Table,
    /** Current navigation context (tenant/realmId) from URL params or global state */
    navigationContext,
  };
};

export default useAppNavigation;
